import _ from 'lodash';

import { actionTypeToTemplateType, userTypeToSegmentType } from 'data/trident-campaigns';
import { nodeTypes } from 'data/trident-types';

const toArray = <T>(value: unknown): T[] => (Array.isArray(value) ? value as T[] : []);

export function getHedwigTemplateUrl(type: string, id: string) {
  return `${window.Grab.config.HEDWIG_DASH_URI}/templates/create/${type}/?id=${id}`;
}

// CDP resolves a segment at /segments/<name>/<entityType>/<namespace>; the
// namespace element is always present, so a name carrying no `<ns>.` prefix
// falls back to `shared` the same way getSegmentNamespace does. Everything
// after the first '.' is part of the name, and both elements are encoded
// because segment names legitimately contain spaces.
export function getSegmentUrl(userType: string, segmentName: string) {
  const name = _.toString(segmentName).replace(/\u00A0/g, ' ');
  const separatorIndex = name.indexOf('.');
  const hasNamespace = separatorIndex > 0;

  const namespace = hasNamespace ? name.slice(0, separatorIndex) : 'shared';
  const id = hasNamespace ? name.slice(separatorIndex + 1) : name;

  const segType = _.get(userTypeToSegmentType, userType, userType);
  return `${window.Grab.config.SEGMENTATION_PLATFORM_URI}/segments/${encodeURIComponent(id)}/${segType}/${encodeURIComponent(namespace)}`;
}

// getCampaignElements returns an CampaignElements object
// the object includes all the event types, action types, hedwig templates, segments used in updateMembership action or bulk action, and time events
export function getCampaignElements(nodes: any) {
  const eventTypes: string[] = [];
  const actionTypes: string[] = [];
  const templates: Array<{ id: string; type: string }> = [];
  const rewardIDs: string[] = [];
  const bulkActionSegments: string[] = [];
  const updateTargetSegments: string[] = [];
  const timeEventMap: Record<string, any> = {}; // key -> {count, meta}

  _.map(nodes, (node: any) => {
    if (node.type === nodeTypes.scenarios) {
      eventTypes.push(...toArray<string>(getEventTypesFromScenariosNode(node)));
    }

    if (node.type === nodeTypes.actions || node.type === nodeTypes.action) {
      // if node type is single action, then simulating it as actions node [journey builder campaign]
      const actionElements = getActionElementsFromActionsNode(
        node.type === nodeTypes.action
          ? { data: [_.get(node, 'data', {})] }
          : node,
      );

      actionTypes.push(...toArray<string>(actionElements.types));
      rewardIDs.push(...toArray<string>(actionElements.rewardIDs));
      templates.push(...toArray<{ id: string; type: string }>(actionElements.templates));
      bulkActionSegments.push(...toArray<string>(actionElements.bulkActionSegments));
      updateTargetSegments.push(...toArray<string>(actionElements.updateTargetSegments));
    }

    if (node.type === nodeTypes.time || node.type === nodeTypes.schedule) {
      const startTime = _.get(node, 'data.startTime');
      const frequency = _.get(node, 'data.frequency');
      const interval = _.get(node, 'data.interval', 1);
      const endTime = _.get(node, 'data.endTime');
      const key = frequency === 'nr' ? `${startTime}-${frequency}` : `${startTime}-${interval}-${frequency}-${endTime}`;

      if (key in timeEventMap) {
        timeEventMap[key].count += 1;
      } else {
        timeEventMap[key] = {
          meta: { startTime, endTime, frequency, interval },
          count: 1,
        };
      }
    }

    if (node.type === nodeTypes.enumeration) {
      const enumElements = getEnumerationElements(node);
      bulkActionSegments.push(...toArray<string>(enumElements.segmentNames));
    }
  });

  return {
    eventTypes: _.uniq(eventTypes),
    actionTypes: _.uniq(actionTypes),
    templates: _.uniqWith(templates, _.isEqual),
    bulkActionSegments: _.uniq(bulkActionSegments),
    updateTargetSegments: _.uniq(updateTargetSegments),
    rewardIDs: _.uniq(rewardIDs),
    timeEvents: _.values(timeEventMap),
  };
}

function getEventTypesFromScenariosNode(node: any): string[] {
  const scenarios = toArray<any>(_.get(node, 'data.scenarios', []));

  return _.map(scenarios, (scenario: any) => scenario.noun);
}

function getActionElementsFromActionsNode(node: any) {
  const actions = toArray<any>(_.get(node, 'data', []));
  const types: string[] = [];
  const templates: Array<{ id: string; type: string }> = []; // array of objects {type, ID}
  const bulkActionSegments: string[] = [];
  const updateTargetSegments: string[] = []; // segments used in updateSegmentMembership
  const rewardIDs: string[] = [];

  _.map(actions, (action: any) => {
    types.push(action.type);

    const templateID = _.get(action, 'meta.messagingPayload.templateID');
    if (templateID) {
      templates.push({
        id: templateID,
        type: _.get(actionTypeToTemplateType, action.type, ''),
      });
    }

    const updateTargetSeg = _.get(action, 'meta.updateSegMembershipPayload.SegmentName');
    if (updateTargetSeg) {
      updateTargetSegments.push(updateTargetSeg);
    }

    const bulkActionSeg = _.get(action, 'meta.segmentName');
    if (bulkActionSeg) {
      bulkActionSegments.push(bulkActionSeg);
    }

    const rewardID = _.get(action, 'meta.rewardID');
    if (rewardID) {
      rewardIDs.push(rewardID);
    }
  });

  return {
    types,
    templates,
    bulkActionSegments,
    updateTargetSegments,
    rewardIDs,
  };
}

function getEnumerationElements(node: any) {
  const segmentNames = _.get(node, 'data.payload.names', []);

  return {
    segmentNames,
  };
}

